import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Lock, Sparkles, Download } from "lucide-react";

// Global paywall interceptor UI. Listens for the `claro:upgrade-required` event
// dispatched by the axios 402 handler (out of AI credits / feature not in plan /
// trial or account locked) and shows a dismissible upgrade message.
export const UpgradeModal = () => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      // Never stack over the trial feedback survey — it would block all its controls.
      if (window.__claroTrialSurveyOpen) return;
      setDetail(e.detail || {});
      setOpen(true);
    };
    const onSurveyOpen = () => setOpen(false);
    window.addEventListener("claro:upgrade-required", handler);
    window.addEventListener("claro:trial-survey-open", onSurveyOpen);
    return () => {
      window.removeEventListener("claro:upgrade-required", handler);
      window.removeEventListener("claro:trial-survey-open", onSurveyOpen);
    };
  }, []);

  const message =
    detail?.message ||
    "This action isn't available on your current plan. Contact your ClaroShield representative to upgrade.";

  let title = "Upgrade required";
  let Icon = Sparkles;
  if (detail?.locked) {
    title = "Account access paused";
    Icon = Lock;
  } else if (detail?.no_credits) {
    title = "You're out of AI credits";
    Icon = Sparkles;
  } else if (detail?.feature === "exports") {
    title = "Exports not included";
    Icon = Download;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="upgrade-modal" className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
              <Icon className="h-5 w-5" />
            </div>
            <DialogTitle data-testid="upgrade-modal-title" className="text-lg">{title}</DialogTitle>
          </div>
        </DialogHeader>
        <DialogDescription data-testid="upgrade-modal-message" className="text-sm text-slate-600 leading-relaxed">
          {message}
        </DialogDescription>
        <DialogFooter>
          <Button
            data-testid="upgrade-modal-dismiss"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            Dismiss
          </Button>
          <Button
            data-testid="upgrade-modal-cta"
            onClick={() => { setOpen(false); navigate("/billing"); }}
          >
            {detail?.no_credits ? "How to add credits" : "How to upgrade"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
